import { CommonModule } from "@angular/common";
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from "@angular/core";
import { Router } from "@angular/router";

import { MessageToastService } from "../../services/message-toast.service";

import { TranslateModule } from "@ngx-translate/core";
import {
  AdvicePointer,
  FEATURE_GROUPS,
  FeatureGroupKey,
  Product,
  ProductName,
} from "@zp-models/product";
import { AdviceService } from "@zp-services/advice.service";
import { ProductService } from "@zp-services/product.service";
import { ShareService } from "@zp-services/share.service";
import { StateService } from "@zp-services/state.service";
import { AdviceBuildUpComponent } from "./advice-build-up/advice-build-up.component";
import { AdviceFooterComponent } from "./advice-footer/advice-footer.component";
import { BusinesscaseComponent } from "./businesscase/businesscase.component";
import { CustomerReferenceComponent } from "./customer-reference/customer-reference.component";
import { ExplanationTileComponent } from "./explanation-tile/explanation-tile.component";
import { KetendienstenComponent } from "./ketendiensten/ketendiensten.component";
import { MessageToastComponent } from "./message-toast/message-toast.component";
import { ProductComparisonComponent } from "./product-comparison/product-comparison.component";
import { SystemCouplingComponent } from "./system-coupling/system-coupling.component";

@Component({
  imports: [
    ProductComparisonComponent,
    TranslateModule,
    CommonModule,
    CustomerReferenceComponent,
    AdviceFooterComponent,
    KetendienstenComponent,
    SystemCouplingComponent,
    BusinesscaseComponent,
    ExplanationTileComponent,
    AdviceBuildUpComponent,
    MessageToastComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: "app-advice",
  templateUrl: "./advice.component.html",
  styleUrls: ["./advice.component.css"],
})
export class AdviceComponent implements OnInit {
  advicedProduct: Product;
  allProducts: Product[] = [];
  featureGroups: FeatureGroupKey[] = FEATURE_GROUPS;

  isMobileView = false;
  expanded: { [key: string]: boolean } = {};
  private readonly contactProductName: ProductName = "Contact";

  advice: AdvicePointer = "Start";

  constructor(
    private _productService: ProductService,
    private shareService: ShareService,
    private stateService: StateService,
    private _adviceService: AdviceService,
    private router: Router,
    private readonly messageToastService: MessageToastService
  ) {
    this.featureGroups.forEach((group) => (this.expanded[group] = false));
  }

  ngOnInit() {
    this.checkMobile();
    this.advice = this._adviceService.getFinalAdvice();
    window.addEventListener("resize", this.checkMobile.bind(this));
    if (!this.advice) {
      console.error("No advice found, redirecting to home page.");
    }
    const foundProduct = this._productService
      .getProducts()
      .find((product) => product.name === this.advice);

    if (foundProduct) {
      this.advicedProduct = foundProduct;
    } else {
      console.error(`Product with name '${this.advice}' not found.`);
      this.router.navigate(["/"]);
      return;
    }
    this.allProducts = this._productService.getProducts();

    this.stateService.setAdvisedProduct(this.advicedProduct.name);
  }

  checkMobile() {
    this.isMobileView = window.innerWidth < 1200;
  }

  orderProduct(name: ProductName) {
    this.stateService.setOrderedProduct(name);
    const targetUrl = "/aanvragen/naw";
    this.router.navigate([targetUrl]);
  }

  shareAdviceLink() {
    const shareUrl = this.shareService.generateAdviesShareLink();
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        this.messageToastService.show("ADVICE.BUSINESS_CASE.COPIED_TO_CLIPBOARD");
      })
      .catch((err) => {
        console.error("Fout bij het kopiëren van de link:", err);
        alert("Kon de link niet kopiëren. Kopieer handmatig: " + shareUrl);
      });
  }

  requestContact() {
    this.orderProduct(this.contactProductName);
  }

  toggleExpanded(group: string) {
    this.expanded[group] = !this.expanded[group];
  }

  getProductNames(): string[] {
    return this.allProducts.map((product) => product.name);
  }

  getFeatureKeys(group: FeatureGroupKey): string[] {
    const featureBlock = this.allProducts[0]?.features[group];
    if (!featureBlock) return [];

    return Object.keys(featureBlock);
  }

  get companyName(): string {
    const company = this.stateService.getOrganisatie();
    return company ? company : "";
  }

  get explanationHeader(): string {
    const name = this.advicedProduct?.name?.toUpperCase();
    return name ? `PRODUCTS.${name}.HEADER` : "";
  }

  get explanationHeaderMobile(): string {
    const name = this.advicedProduct?.name?.toUpperCase();
    return name ? `PRODUCTS.${name}.HEADER_MOBILE` : "";
  }

  get explanationText(): string {
    const name = this.advicedProduct?.name?.toUpperCase();
    return name ? `PRODUCTS.${name}.SUB_HEADER` : "";
  }
}
